import React, { useState } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Router } from 'react-router-dom'
import { PlatformIdentityProvider } from '../../providers/platform-identity-provider/platform-identity-provider'
import { ThemeProvider } from '../../providers/theme-provider/theme-provider'
import { ThemePreferenceSync } from '../../providers/theme-provider/theme-preference-sync'
import { UserProvider } from '../../providers/user-provider/user-provider'
import { AccessOwnerProvider } from '../access/access-owner-provider'
import { getAccessOwner } from '../access/access-singleton'
import { AccessUrlSync } from './access-url-sync'
import { getAppBootActor } from './boot-actor'
import { BootGate } from './boot-gate'
import { BootProvider } from './boot-provider'
import { AppErrorBoundary } from './error-boundary'
import { createAppQueryClient } from './query-client'

type RouterProps = React.ComponentProps<typeof Router>

// The router's typings predate implicit-children removal, so children are declared here.
const HistoryRouter = Router as React.ComponentType<React.PropsWithChildren<RouterProps>>

export type AppRootProps = {
    /** The application. It mounts when boot is ready, and also when there is no valid session so it can sign the user in. */
    app: React.ReactNode
    /** Routes with this history instead of the browser's. For tests. */
    history?: RouterProps['history']
}

/**
 * The production tree. The two loaders sit above the gate because a gate that hides its own
 * loaders can never open; they render their children in every phase and the gate decides what the
 * user sees. Both phases that mount the app render the same subtree, so a logout does not remount it.
 *
 * The access owner is delivered above the root boundary so every layer, the boundaries included,
 * reads it with `useAccessOwner()`. The query client is mounted for screen data to adopt; nothing
 * reads it yet.
 */
export const AppRoot = ({ app, history }: AppRootProps) => {
    const [queryClient] = useState(createAppQueryClient)
    const appSubtree = (
        <>
            <ThemePreferenceSync />
            <AccessUrlSync />
            {app}
        </>
    )
    const tree = (
        <AccessOwnerProvider owner={getAccessOwner()}>
            <AppErrorBoundary layer="root">
                <BootProvider actor={getAppBootActor()}>
                    <PlatformIdentityProvider>
                        <ThemeProvider>
                            <UserProvider>
                                <AppErrorBoundary layer="boot">
                                    <QueryClientProvider client={queryClient}>
                                        <BootGate unauthenticated={appSubtree}>{appSubtree}</BootGate>
                                    </QueryClientProvider>
                                </AppErrorBoundary>
                            </UserProvider>
                        </ThemeProvider>
                    </PlatformIdentityProvider>
                </BootProvider>
            </AppErrorBoundary>
        </AccessOwnerProvider>
    )

    return history ? <HistoryRouter history={history}>{tree}</HistoryRouter> : <BrowserRouter>{tree}</BrowserRouter>
}
